import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
} from '~/components/SolaForceDataGrid';
import {
  CustomDataFieldAttributes,
  useCustomDataAttributes,
} from '~/data/customData';
import { useAllEmployees } from '~/data/employees';
import { AllEnums, EnumListKey, useAllEnums } from '~/data/enums';
import { CustomDataReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import {
  browserLogError,
  getEnumName,
  getNumberOrUndefined,
} from '~/utils/helper';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type CustomDataReportRow = {
  employeeId: number;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  legalEntityName: string;
  legalEntityCode: string;
  country: string;
  unitName: string;
  unitNumber: string;
  emplStartDate: string; // Date
  empContractIsPrimary: boolean;
  personnelGroup1?: string;
  personnelGroup2?: string;
  personnelGroup3?: string;
  personnelGroup4?: string;
  personnelGroup5?: string;
  [key: string]: unknown;
};

type PropsType = {
  data: CustomDataReportData[];
};

type CustomDataEntries = {
  fCustomDataFieldName: string;
  fCustomDataFieldValue: string;
  fCustomDataFieldLabel: string;
};

function getCustomFieldValue(
  customDataFieldAttributes: CustomDataFieldAttributes,
  entriesIndexed: Record<string, CustomDataEntries[]>,
  allEnums: AllEnums,
) {
  try {
    if (!customDataFieldAttributes) {
      return '';
    }
    const strName = customDataFieldAttributes.fCustomDataAttributeName;
    const strType = customDataFieldAttributes.fCustomDataAttributeDataType;
    if (!entriesIndexed.hasOwnProperty(strName)) {
      return '';
    }
    if (strType === 'STRING') {
      return entriesIndexed[strName][0].fCustomDataFieldValue;
    }
    if (strType === 'DATE') {
      return convertToDateOrUndefined(
        entriesIndexed[strName][0].fCustomDataFieldValue,
      );
    }
    if (strType === 'BOOLEAN') {
      return entriesIndexed[strName][0].fCustomDataFieldValue;
    }
    if (strType === 'NUMBER') {
      return getNumberOrUndefined(
        entriesIndexed[strName][0].fCustomDataFieldValue,
      );
    }
    if (strType === 'TEXT') {
      return entriesIndexed[strName][0].fCustomDataFieldValue;
    }
    if (strType === 'ENUM') {
      return getEnumName(
        allEnums[
          customDataFieldAttributes.fCustomDataAttributeDataSubtype || ''
        ],
        entriesIndexed[strName][0].fCustomDataFieldValue,
      );
    }
    if (strType === 'MULTISELECT') {
      return entriesIndexed[strName]
        .map((item) =>
          getEnumName(
            allEnums[
              customDataFieldAttributes.fCustomDataAttributeDataSubtype || ''
            ],
            String(item.fCustomDataFieldValue),
          ),
        )
        .join(',');
    }
    return '';
  } catch (err) {
    browserLogError('getCustomFieldValue', err);
  }
}

export const useCustomDataReport = (props: PropsType) => {
  const { data } = props;

  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEmployeeById } = useAllEmployees();
  const { getEnums, getCustomDataEnums, allEnums } = useAllEnums();

  const { data: customDataAttributes } = useCustomDataAttributes();
  const customDataAttributesIndexed = useMemo(
    () =>
      customDataAttributes.reduce(
        (acc, item) => ({ ...acc, [item.fCustomDataAttributeName]: item }),
        {} as Record<string, CustomDataFieldAttributes>,
      ),
    [customDataAttributes],
  );

  const customDataEnums = getCustomDataEnums('CUSTOM_ENTITY_EMPLOYEE');

  const enums = useMemo(() => {
    return {
      personnelGroup1: getEnums(EnumListKey.PERSONNEL_GROUP_1),
      personnelGroup2: getEnums(EnumListKey.PERSONNEL_GROUP_2),
      personnelGroup3: getEnums(EnumListKey.PERSONNEL_GROUP_3),
      personnelGroup4: getEnums(EnumListKey.PERSONNEL_GROUP_4),
      personnelGroup5: getEnums(EnumListKey.PERSONNEL_GROUP_5),
    };
  }, [getEnums]);

  const rows = useMemo(() => {
    const {
      personnelGroup1,
      personnelGroup2,
      personnelGroup3,
      personnelGroup4,
      personnelGroup5,
    } = enums;

    return data.map((dataEntry: CustomDataReportData): CustomDataReportRow => {
      const {
        fEmpId,
        fEmpNumber,
        fEmpLegEntName,
        fEmpLegEntCode,
        fEmpOfficeCountry,
        fEmpUnitName,
        fEmpUnitCode,
        fEmplStartDate,
        fEmpContractIsPrimary,
        fEmpPersonnelGroups,
        fDataEntriesEmp,
      } = dataEntry;

      const employee = getEmployeeById(fEmpId);

      const customDataFieldsIndexed = fDataEntriesEmp.reduce((acc, data) => {
        return {
          ...acc,
          [data.fCustomDataFieldName]: acc[data.fCustomDataFieldName]
            ? [...acc[data.fCustomDataFieldName], data]
            : [data],
        };
      }, {} as Record<string, CustomDataEntries[]>);

      const customFieldsValues = customDataEnums.reduce((acc, item) => {
        return {
          ...acc,
          [item.code]: getCustomFieldValue(
            customDataAttributesIndexed[item.code],
            customDataFieldsIndexed,
            allEnums,
          ),
        };
      }, {});

      return {
        employeeId: fEmpId,
        employeeNumber: fEmpNumber,
        firstName: employee?.fEmpFirstName || '',
        lastName: employee?.fEmpLastName || '',
        legalEntityName: fEmpLegEntName,
        legalEntityCode: fEmpLegEntCode,
        country: fEmpOfficeCountry,
        unitName: fEmpUnitName,
        unitNumber: fEmpUnitCode,
        emplStartDate: fEmplStartDate,
        empContractIsPrimary: fEmpContractIsPrimary,
        personnelGroup1: getEnumName(
          personnelGroup1,
          fEmpPersonnelGroups.fEmpPersonnelGroup1,
        ),
        personnelGroup2: getEnumName(
          personnelGroup2,
          fEmpPersonnelGroups.fEmpPersonnelGroup2,
        ),
        personnelGroup3: getEnumName(
          personnelGroup3,
          fEmpPersonnelGroups.fEmpPersonnelGroup3,
        ),
        personnelGroup4: getEnumName(
          personnelGroup4,
          fEmpPersonnelGroups.fEmpPersonnelGroup4,
        ),
        personnelGroup5: getEnumName(
          personnelGroup5,
          fEmpPersonnelGroups.fEmpPersonnelGroup5,
        ),
        ...customFieldsValues,
      };
    });
  }, [data, enums]);

  const columns = useMemo((): GridColDef<CustomDataReportRow>[] => {
    const customDataHeaders = customDataEnums.map((item) => ({
      field: item.code,
      headerName: item.name,
      minWidth: 210,
    }));

    return [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'unitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpNumber'),
        minWidth: 130,
      },
      {
        field: 'country',
        headerName: t('laCountry'),
        minWidth: 130,
      },
      {
        field: 'legalEntityName',
        headerName: t('laLegalEntity'),
        minWidth: 210,
      },
      {
        field: 'legalEntityCode',
        headerName: t('laLegalEntityCode'),
        minWidth: 210,
      },
      {
        field: 'personnelGroup1',
        headerName: t('fPersonnelGroup1'),
        minWidth: 210,
      },
      {
        field: 'personnelGroup2',
        headerName: t('fPersonnelGroup2'),
        minWidth: 210,
      },
      {
        field: 'personnelGroup3',
        headerName: t('fPersonnelGroup3'),
        minWidth: 210,
      },
      {
        field: 'personnelGroup4',
        headerName: t('fPersonnelGroup4'),
        minWidth: 210,
      },
      {
        field: 'personnelGroup5',
        headerName: t('fPersonnelGroup5'),
        minWidth: 210,
      },
      ...customDataHeaders,
    ];
  }, [t, enums, customDataEnums]);

  return {
    rows,
    columns,
  };
};
